"use strict";
// Copyright (c) 2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScriptOpIter = exports.Script = void 0;
const varsize_js_1 = require("./io/varsize.js");
const writerlength_js_1 = require("./io/writerlength.js");
const writerbytes_js_1 = require("./io/writerbytes.js");
const hex_js_1 = require("./io/hex.js");
const op_js_1 = require("./op.js");
const opcode_js_1 = require("./opcode.js");
const bytes_js_1 = require("./io/bytes.js");
const consts_js_1 = require("./consts.js");
const address_1 = require("./address/address");
/** A Bitcoin Script locking/unlocking a UTXO */
class Script {
    /** Create a new Script with the given bytecode or empty */
    constructor(bytecode) {
        this.bytecode = bytecode ?? new Uint8Array();
    }
    /**
     * Write the script to the writer with the script size as VARINT
     * prepended.
     **/
    writeWithSize(writer) {
        (0, varsize_js_1.writeVarSize)(this.bytecode.length, writer);
        writer.putBytes(this.bytecode);
    }
    static readWithSize(bytes) {
        const size = (0, varsize_js_1.readVarSize)(bytes);
        return new Script(bytes.readBytes(Number(size)));
    }
    /** Build a Script from the given Script Ops */
    static fromOps(ops) {
        let scriptSize = 0;
        for (const op of ops) {
            const writerLength = new writerlength_js_1.WriterLength();
            (0, op_js_1.writeOp)(op, writerLength);
            scriptSize += writerLength.length;
        }
        const bytecodeWriter = new writerbytes_js_1.WriterBytes(scriptSize);
        for (const op of ops) {
            (0, op_js_1.writeOp)(op, bytecodeWriter);
        }
        return new Script(bytecodeWriter.data);
    }
    static fromAddress(address) {
        // make Address from address
        const thisAddress = address_1.Address.fromCashAddress(address);
        switch (thisAddress.type) {
            case 'p2pkh': {
                return Script.p2pkh((0, hex_js_1.fromHex)(thisAddress.hash));
            }
            case 'p2sh': {
                return Script.p2sh((0, hex_js_1.fromHex)(thisAddress.hash));
            }
            default: {
                // Note we should never get here, as Address constructor
                // only supports p2pkh and p2sh
                throw new Error(`Unsupported address type: ${thisAddress.type}`);
            }
        }
    }
    /** Iterate over the Ops of this Script */
    ops() {
        return new ScriptOpIter(new bytes_js_1.Bytes(this.bytecode));
    }
    /** Create a deep copy of this Script */
    copy() {
        return new Script(new Uint8Array(this.bytecode));
    }
    /**
     * Find the n-th OP_CODESEPARATOR (0-based) and cut out the bytecode
     * following it. Required for signing BIP143 scripts that have an
     * OP_CODESEPARATOR.
     *
     * Throw an error if the n-th OP_CODESEPARATOR doesn't exist.
     *
     * Historically this opcode has been seen as obscure and useless, but in
     * BIP143 sighash-based covenants, basically every covenant benefits from
     * its usage, by trimming down the sighash preimage size and thus tx size.
     *
     * Really long Scripts will have a big BIP143 preimage, which costs precious
     * bytes (and the preimage might even go over the 520 pushdata limit).
     * This can be trimmed down to just one single byte by ending the covenant
     * in `... OP_CODESEPARATOR OP_CHECKSIG`, in which case the BIP143 signature
     * algo will cut out everything after the OP_CODESEPARATOR, so only the
     * OP_CHECKSIG remains.
     * If the covenant bytecode is 520 or so, this would save 519 bytes.
     */
    cutOutCodesep(nCodesep) {
        const ops = this.ops();
        let op;
        let nCodesepsFound = 0;
        while ((op = ops.next()) !== undefined) {
            if (op == opcode_js_1.OP_CODESEPARATOR) {
                if (nCodesepsFound == nCodesep) {
                    return new Script(this.bytecode.slice(ops.bytes.idx));
                }
                nCodesepsFound++;
            }
        }
        throw new Error('OP_CODESEPARATOR not found');
    }
    /**
     * Whether the Script is a P2SH Script.
     * Matches CScript::IsPayToScriptHash in /src/script/script.h.
     **/
    isP2sh() {
        if (this.bytecode.length != 23) {
            return false;
        }
        return (this.bytecode[0] == opcode_js_1.OP_HASH160 &&
            this.bytecode[1] == 20 &&
            this.bytecode[22] == opcode_js_1.OP_EQUAL);
    }
    /**
     * Return hex string of this Script's bytecode
     */
    toHex() {
        return (0, hex_js_1.toHex)(this.bytecode);
    }
    /** Build a P2SH script for the given script hash */
    static p2sh(scriptHash) {
        if (scriptHash.length !== 20) {
            throw new Error(`scriptHash length must be 20, got ${scriptHash.length}`);
        }
        return Script.fromOps([opcode_js_1.OP_HASH160, (0, op_js_1.pushBytesOp)(scriptHash), opcode_js_1.OP_EQUAL]);
    }
    /** Build a P2PKH script for the given public key hash */
    static p2pkh(pkh) {
        if (pkh.length !== 20) {
            throw new Error(`pkh length must be 20, got ${pkh.length}`);
        }
        return Script.fromOps([
            opcode_js_1.OP_DUP,
            opcode_js_1.OP_HASH160,
            (0, op_js_1.pushBytesOp)(pkh),
            opcode_js_1.OP_EQUALVERIFY,
            opcode_js_1.OP_CHECKSIG,
        ]);
    }
    /** Build a scriptSig for spending a P2PKH output */
    static p2pkhSpend(pk, sig) {
        return Script.fromOps([(0, op_js_1.pushBytesOp)(sig), (0, op_js_1.pushBytesOp)(pk)]);
    }
    /**
     * Build m-of-n multisig script: OP_m pubkey1 pubkey2 ... pubkey_n OP_n OP_CHECKMULTISIG.
     * Works for P2SH-wrapped or bare multisig.
     * Pubkeys are used in the order given; callers who want canonical (sorted) multisig
     * must sort pubkeys before calling.
     */
    static multisig(minNumPks, pubkeys) {
        const numPubkeys = pubkeys.length;
        if (minNumPks < 1) {
            throw new Error(`minNumPks must be >= 1 (got ${minNumPks})`);
        }
        if (minNumPks > numPubkeys) {
            throw new Error(`minNumPks must be <= numPubkeys (got ${minNumPks} of ${numPubkeys})`);
        }
        if (numPubkeys > consts_js_1.MAX_PUBKEYS_PER_MULTISIG) {
            throw new Error(`numPubkeys must be <= ${consts_js_1.MAX_PUBKEYS_PER_MULTISIG} (got ${numPubkeys})`);
        }
        const ops = [
            (0, op_js_1.pushNumberOp)(minNumPks),
            ...pubkeys.map(pk => (0, op_js_1.pushBytesOp)(pk)),
            (0, op_js_1.pushNumberOp)(numPubkeys),
            opcode_js_1.OP_CHECKMULTISIG,
        ];
        return Script.fromOps(ops);
    }
    /**
     * Return true iff this script has the shape of a standard multisig output script
     * `OP_m pubkey_1 ... pubkey_N OP_N OP_CHECKMULTISIG` (fixed size: `N + 3` ops).
     */
    isMultisig() {
        const ops = [];
        const iter = this.ops();
        let op;
        while ((op = iter.next()) !== undefined) {
            ops.push(op);
        }
        if (ops.length < 4 || ops[ops.length - 1] !== opcode_js_1.OP_CHECKMULTISIG) {
            return false;
        }
        try {
            (0, op_js_1.parseNumberFromOp)(ops[0]);
            const numPubkeys = Number((0, op_js_1.parseNumberFromOp)(ops[ops.length - 2]));
            return ops.length === numPubkeys + 3;
        }
        catch {
            return false;
        }
    }
    /**
     * Build scriptSig for multisig: <dummy> sig1 sig2 ... sig_m [redeemScript].
     * Omit redeemScript for bare multisig; include for P2SH-wrapped.
     * Use undefined for missing signatures (replaced with 0x01 placeholder).
     * For Schnorr sigs, pass pubkeyIndices (set of signer indices) to build the
     * checkbits dummy; signatures must be ordered by sorted pubkeyIndices.
     * The full set of signer indices must be known when building Schnorr format.
     * For partially signed inputs where the final signers are not yet known,
     * use ECDSA format (omit pubkeyIndices) until the full signer set is determined.
     * Ref spec https://github.com/bitcoincashorg/bitcoincash.org/blob/master/spec/2019-11-15-schnorrmultisig.md
     */
    static multisigSpend(params) {
        const { signatures, redeemScript, pubkeyIndices, numPubkeys } = params;
        const PLACEHOLDER = new Uint8Array([0x01]);
        let dummyOp;
        if (pubkeyIndices !== undefined) {
            const nVal = numPubkeys ??
                (redeemScript !== undefined
                    ? redeemScript.parseMultisigRedeemScript().numPubkeys
                    : undefined);
            if (nVal === undefined) {
                throw new Error('pubkeyIndices requires redeemScript or numPubkeys to derive checkbits');
            }
            const mVal = redeemScript
                ? redeemScript.parseMultisigRedeemScript().numSignatures
                : signatures.length;
            if (pubkeyIndices.size !== mVal) {
                throw new Error(`pubkeyIndices must have ${mVal} elements for ${mVal}-of-${nVal}`);
            }
            for (const i of pubkeyIndices) {
                if (i < 0 || i >= nVal) {
                    throw new Error(`pubkeyIndices index ${i} out of range [0, ${nVal})`);
                }
            }
            dummyOp = (0, op_js_1.pushBytesOp)(checkbitsFromPubkeyIndices(pubkeyIndices, nVal));
        }
        else {
            dummyOp = opcode_js_1.OP_0;
        }
        const ops = [
            dummyOp,
            ...signatures.map(sig => (0, op_js_1.pushBytesOp)(sig !== undefined ? sig : PLACEHOLDER)),
        ];
        if (redeemScript !== undefined) {
            ops.push((0, op_js_1.pushBytesOp)(redeemScript.bytecode));
        }
        return Script.fromOps(ops);
    }
    /**
     * Parse P2SH multisig spend script to extract signatures and redeem script.
     * Returns signatures (undefined for placeholder) and parsed numSignatures, numPubkeys from redeem script.
     * Supports both ECDSA (OP_0 + sigs) and Schnorr (checkbits + sigs) formats.
     * Schnorr format accepts partial scriptSigs (checkbits may have fewer bits set than
     * numSignatures when earlier signers may not yet know who else will sign).
     * Not for bare multisig (where the locking script is in output, not input).
     */
    parseP2shMultisigSpend() {
        const ops = [];
        const iter = this.ops();
        let op;
        while ((op = iter.next()) !== undefined) {
            ops.push(op);
        }
        if (ops.length < 3) {
            throw new Error('Invalid multisig scriptSig: too few ops');
        }
        const redeemScriptOp = ops[ops.length - 1];
        if (!(0, op_js_1.isPushOp)(redeemScriptOp)) {
            throw new Error('Invalid multisig scriptSig: redeem script must be final push');
        }
        const redeemScript = new Script(redeemScriptOp.data);
        const parsed = Script.parseMultisigScriptSig(ops.slice(0, -1), redeemScript);
        return { ...parsed, redeemScript };
    }
    /**
     * Parse bare multisig spend scriptSig to extract signatures.
     * For bare multisig the output script is the multisig script; the scriptSig
     * is [dummy] sig1 sig2 ... sig_m with no redeem script.
     * Assumes a fully-formed scriptSig (see parseP2shMultisigSpend for details).
     * @param outputScript - The multisig output script (OP_m pubkey1 ... pubkey_n OP_n OP_CHECKMULTISIG)
     */
    parseBareMultisigSpend(outputScript) {
        const ops = [];
        const iter = this.ops();
        let op;
        while ((op = iter.next()) !== undefined) {
            ops.push(op);
        }
        return Script.parseMultisigScriptSig(ops, outputScript);
    }
    /** Parse an OP_m ... OP_CHECKMULTISIG redeem script (used by multisig PSBT flows). */
    parseMultisigRedeemScript() {
        const ops = [];
        const iter = this.ops();
        let op;
        while ((op = iter.next()) !== undefined) {
            ops.push(op);
        }
        if (ops.length < 4) {
            throw new Error('Invalid multisig redeem script');
        }
        const first = ops[0];
        const lastBeforeCheck = ops[ops.length - 2];
        const numSignatures = Number((0, op_js_1.parseNumberFromOp)(first));
        const numPubkeys = Number((0, op_js_1.parseNumberFromOp)(lastBeforeCheck));
        const pubkeys = ops
            .slice(1, -2)
            .filter((op) => (0, op_js_1.isPushOp)(op))
            .map(op => op.data);
        if (pubkeys.length !== numPubkeys) {
            throw new Error(`Invalid multisig redeem script: expected ${numPubkeys} pubkeys, got ${pubkeys.length}`);
        }
        if (!Number.isSafeInteger(numSignatures) ||
            !Number.isSafeInteger(numPubkeys)) {
            throw new Error(`Invalid multisig redeem script: m and n must be safe integers (got m=${numSignatures}, n=${numPubkeys})`);
        }
        if (numSignatures < 1 || numPubkeys < 1) {
            throw new Error(`Invalid multisig redeem script: m and n must be >= 1 (got m=${numSignatures}, n=${numPubkeys})`);
        }
        if (numSignatures > numPubkeys) {
            throw new Error(`Invalid multisig redeem script: m must be <= n (got ${numSignatures} of ${numPubkeys})`);
        }
        if (numPubkeys > consts_js_1.MAX_PUBKEYS_PER_MULTISIG) {
            throw new Error(`Invalid multisig redeem script: n must be <= ${consts_js_1.MAX_PUBKEYS_PER_MULTISIG} (got ${numPubkeys})`);
        }
        return { numSignatures, numPubkeys, pubkeys };
    }
    static parseMultisigScriptSig(ops, outputScript) {
        const { numSignatures, numPubkeys, pubkeys } = outputScript.parseMultisigRedeemScript();
        if (ops.length !== numSignatures + 1) {
            throw new Error(`Invalid multisig scriptSig: expected ${numSignatures + 1} ops (dummy + ${numSignatures} sigs), got ${ops.length}`);
        }
        const expectedCheckbitsLen = Math.floor((numPubkeys + 7) / 8);
        let isSchnorr;
        let sigOps;
        let pubkeyIndices;
        if (ops[0] === opcode_js_1.OP_0) {
            isSchnorr = false;
            sigOps = ops.slice(1);
        }
        else {
            let checkbitsData;
            const firstOp = ops[0];
            if (typeof firstOp === 'number' &&
                firstOp >= opcode_js_1.OP_1 &&
                firstOp <= opcode_js_1.OP_16) {
                checkbitsData = new Uint8Array([firstOp - 0x50]);
            }
            else if ((0, op_js_1.isPushOp)(firstOp) && firstOp.data.length > 0) {
                checkbitsData = firstOp.data;
            }
            else {
                throw new Error('Invalid multisig scriptSig: must start with OP_0 (ECDSA) or checkbits push (Schnorr)');
            }
            if (checkbitsData.length !== expectedCheckbitsLen) {
                throw new Error(`Invalid Schnorr multisig: checkbits length ${checkbitsData.length} != expected ${expectedCheckbitsLen} for numPubkeys=${numPubkeys}`);
            }
            isSchnorr = true;
            pubkeyIndices = checkbitsToIndices(checkbitsData, numPubkeys);
            sigOps = ops.slice(1);
        }
        if (sigOps.length !== numSignatures) {
            throw new Error(`Invalid multisig scriptSig: expected ${numSignatures} signatures, got ${sigOps.length}`);
        }
        const signatures = sigOps.map(op => {
            if (!(0, op_js_1.isPushOp)(op))
                return undefined;
            return op.data.length === 1 && op.data[0] === 0x01
                ? undefined
                : op.data;
        });
        return {
            signatures,
            numSignatures,
            numPubkeys,
            pubkeys,
            isSchnorr,
            ...(pubkeyIndices !== undefined && { pubkeyIndices }),
        };
    }
}
exports.Script = Script;
/**
 * Build checkbits byte array from pubkey indices for Schnorr multisig.
 * Spec: length = floor((N+7)/8), little-endian, bit i = 1 iff index i in set.
 */
function checkbitsFromPubkeyIndices(indices, numPubkeys) {
    const numBytes = Math.floor((numPubkeys + 7) / 8);
    const bytes = new Uint8Array(numBytes);
    for (const i of indices) {
        bytes[i >>> 3] |= 1 << (i & 7);
    }
    return bytes;
}
/**
 * Parse checkbits byte array to set of pubkey indices for Schnorr multisig.
 * Inverse of checkbitsFromPubkeyIndices.
 */
function checkbitsToIndices(checkbits, numPubkeys) {
    const indices = new Set();
    for (let i = 0; i < numPubkeys; i++) {
        if ((checkbits[i >>> 3] & (1 << (i & 7))) !== 0) {
            indices.add(i);
        }
    }
    return indices;
}
/** Iterator over the Ops of a Script. */
class ScriptOpIter {
    constructor(bytes) {
        this.bytes = bytes;
    }
    /**
     * Read the next Op and return it, or `undefined` if there are no more Ops.
     * Throws an error if reading the next op failed.
     */
    next() {
        if (this.bytes.idx >= this.bytes.data.length) {
            return undefined;
        }
        return (0, op_js_1.readOp)(this.bytes);
    }
}
exports.ScriptOpIter = ScriptOpIter;
//# sourceMappingURL=script.js.map